/**
 * @page ReportsPage
 * @description Advanced aggregate reports — daily/weekly/monthly
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';

type ReportTab = 'daily' | 'weekly' | 'monthly';

interface AggregateReport {
  periodLabel: string;
  startDate: string;
  endDate: string;
  totalIncome: string;
  totalExpense: string;
  netAmount: string;
  nonCashIncome?: string;
  nonCashExpense?: string;
  transactionCount: number;
  tournamentCount: number;
  sessionCount: number;
  sessionIds: string[];
  categoryBreakdown: Array<{ category: string; type: string; total: string; count: number }>;
}

function getToday() {
  return new Date(Date.now() + 8 * 3600000).toISOString().slice(0, 10);
}

function getMonday() {
  const d = new Date(`${getToday()}T00:00:00Z`);
  const day = d.getUTCDay();
  d.setUTCDate(d.getUTCDate() - (day === 0 ? 6 : day - 1));
  return d.toISOString().slice(0, 10);
}

const safeNum = (v: string) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

export default function ReportsPage() {
  const { t } = useTranslation('accounting');
  const { t: tc } = useTranslation('common');
  const [tab, setTab] = useState<ReportTab>('daily');
  const [date, setDate] = useState(getToday());
  const [weekStart, setWeekStart] = useState(getMonday());
  const [year, setYear] = useState(Number(getToday().slice(0, 4)));
  const [month, setMonth] = useState(Number(getToday().slice(5, 7)));

  const queryKey =
    tab === 'daily'
      ? ['report', 'daily', date]
      : tab === 'weekly'
        ? ['report', 'weekly', weekStart]
        : ['report', 'monthly', year, month];

  const queryFn = () => {
    if (tab === 'daily') return api.get<AggregateReport>(`/accounting/reports/daily?date=${date}`);
    if (tab === 'weekly')
      return api.get<AggregateReport>(`/accounting/reports/weekly?weekStart=${weekStart}`);
    return api.get<AggregateReport>(`/accounting/reports/monthly?year=${year}&month=${month}`);
  };

  const { data: report, isLoading } = useQuery<AggregateReport>({
    queryKey,
    queryFn,
  });

  const tabs: { key: ReportTab; label: string }[] = [
    { key: 'daily', label: t('reportDaily', { defaultValue: 'Daily' }) },
    { key: 'weekly', label: t('reportWeekly', { defaultValue: 'Weekly' }) },
    { key: 'monthly', label: t('reportMonthly', { defaultValue: 'Monthly' }) },
  ];

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <h1 className="text-2xl font-bold text-base-content mb-6">
        {t('advancedReports', { defaultValue: 'Reports' })}
      </h1>

      {/* Tab nav */}
      <div className="flex gap-2 mb-6">
        {tabs.map((tb) => (
          <button
            key={tb.key}
            onClick={() => setTab(tb.key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              tab === tb.key
                ? 'bg-primary text-primary-content'
                : 'bg-base-300 text-base-content/70 hover:bg-base-300/80'
            }`}
          >
            {tb.label}
          </button>
        ))}
      </div>

      {/* Date picker */}
      <div className="mb-6">
        {tab === 'daily' && (
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="px-3 py-2 rounded-lg border border-base-300 bg-base-200 text-base-content text-sm"
          />
        )}
        {tab === 'weekly' && (
          <input
            type="date"
            value={weekStart}
            onChange={(e) => setWeekStart(e.target.value)}
            className="px-3 py-2 rounded-lg border border-base-300 bg-base-200 text-base-content text-sm"
          />
        )}
        {tab === 'monthly' && (
          <div className="flex gap-2">
            <input
              type="number"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              min={2020}
              max={2030}
              className="w-24 px-3 py-2 rounded-lg border border-base-300 bg-base-200 text-base-content text-sm"
            />
            <select
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              className="px-3 py-2 rounded-lg border border-base-300 bg-base-200 text-base-content text-sm"
            >
              {Array.from({ length: 12 }, (_, i) => (
                <option key={i + 1} value={i + 1}>
                  {i + 1}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-base-content/40">{tc('loading')}</div>
      ) : report ? (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-base-200 border border-base-300 rounded-xl p-4 text-center">
              <div className="text-xs text-base-content/60 mb-1">
                {t('totalIncome', { defaultValue: 'Income' })}
              </div>
              <div className="text-lg font-bold text-success font-mono">
                NT${safeNum(report.totalIncome).toLocaleString()}
              </div>
            </div>
            <div className="bg-base-200 border border-base-300 rounded-xl p-4 text-center">
              <div className="text-xs text-base-content/60 mb-1">
                {t('totalExpense', { defaultValue: 'Expense' })}
              </div>
              <div className="text-lg font-bold text-error font-mono">
                NT${safeNum(report.totalExpense).toLocaleString()}
              </div>
            </div>
            <div className="bg-base-200 border border-base-300 rounded-xl p-4 text-center">
              <div className="text-xs text-base-content/60 mb-1">
                {t('netAmount', { defaultValue: 'Net' })}
              </div>
              <div
                className={`text-lg font-bold font-mono ${safeNum(report.netAmount) >= 0 ? 'text-success' : 'text-error'}`}
              >
                NT${safeNum(report.netAmount).toLocaleString()}
              </div>
            </div>
          </div>

          <p className="mb-4 text-sm">
            {t('noncashTotals', {
              income: report.nonCashIncome ?? '0.00',
              expense: report.nonCashExpense ?? '0.00',
            })}
          </p>
          {/* Stats + PDF */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex gap-4 text-sm text-base-content/60">
              <span>
                {t('transactionCount', { defaultValue: 'Transactions' })}: {report.transactionCount}
              </span>
              <span>
                {t('tournamentCount', { defaultValue: 'Tournaments' })}: {report.tournamentCount}
              </span>
              <span>
                {t('sessionCount', { defaultValue: 'Sessions' })}: {report.sessionCount}
              </span>
            </div>
            {tab === 'daily' && report?.sessionIds?.[0] && (
              <button
                onClick={async () => {
                  const sessionId = report.sessionIds[0];
                  try {
                    const res = await api.get<Blob>(`/accounting/sessions/${sessionId}/pdf`, {
                      responseType: 'blob' as never,
                    });
                    const blob =
                      res instanceof Blob
                        ? res
                        : new Blob([res as never], { type: 'application/pdf' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `settlement-${date}.pdf`;
                    a.click();
                    URL.revokeObjectURL(url);
                  } catch {
                    /* guard already handles via interceptor */
                  }
                }}
                className="px-3 py-1.5 rounded-lg border border-base-300 text-sm hover:bg-base-300 transition-colors"
              >
                PDF
              </button>
            )}
          </div>

          {/* Category breakdown */}
          {report.categoryBreakdown.length > 0 && (
            <div className="bg-base-200 border border-base-300 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-base-300/50">
                    <th className="px-4 py-2 text-left">
                      {t('category', { defaultValue: 'Category' })}
                    </th>
                    <th className="px-4 py-2 text-left">{t('type', { defaultValue: 'Type' })}</th>
                    <th className="px-4 py-2 text-right">
                      {t('total', { defaultValue: 'Total' })}
                    </th>
                    <th className="px-4 py-2 text-right">
                      {t('count', { defaultValue: 'Count' })}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {report.categoryBreakdown.map((c, i) => (
                    <tr key={i} className="border-t border-base-300">
                      <td className="px-4 py-2">{c.category}</td>
                      <td className="px-4 py-2">
                        <span className={c.type === 'INCOME' ? 'text-success' : 'text-error'}>
                          {c.type}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-right font-mono">
                        NT${safeNum(c.total).toLocaleString()}
                      </td>
                      <td className="px-4 py-2 text-right">{c.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-12 text-base-content/40">
          {t('noReportData', { defaultValue: 'No data for this period' })}
        </div>
      )}
    </div>
  );
}
